import { useAppRouter } from '@/common/route/useAppRouter';
import { timeUnits } from '@/common/utils/helpers';
import { StringUtils } from '@/common/utils/stringUtils';
import { validateDateAfter, validateFormat } from '@/common/validator/validatorHelpers';
import { useCrudActions } from '@/common/view/useCrudActions';
import { ROUTE_NAME_SCRATCH } from '@/modules/scratch/router';
import { scratchTicketService } from '@/services/scratch';
import useVuelidate from '@vuelidate/core';
import { minValue, numeric, required, requiredIf } from '@vuelidate/validators';
import { reactive } from 'vue';
import { useRoute } from 'vue-router';

export function useTicketEdit() {
    const route = useRoute();
    const router = useAppRouter();
    const crudAction = useCrudActions();

    const model = reactive({
        id: StringUtils.toNumber(route.params.id),
        freeLimit: null,
        type: null,
        cost: null,
        price: null,
        packageId: null,
        adsView: null,
        quantity: null,
        baseValue: 0,
        payoutRate: null,
        adsPayoutRate: null,
        displayRate: null,
        timeUnit: null,
        timeDuration: null,
        startTime: new Date(),
        endTime: null
    });

    const validations = {
        freeLimit: { required, numeric, minValue: minValue(0) },
        quantity: { required, numeric, minValue: minValue(0) },
        payoutRate: {
            required,
            validateFormat: validateFormat(
                /^\d+(.\d+)?:\d+(.\d+)?(,\d+(.\d+)?:\d+(.\d+)?)*$/g,
                `Please enter in the correct format: X1:Y1,X2:Y2... Ex: 0.3:10,4:100`
            )
        },
        adsPayoutRate: {
            requiredIf: requiredIf(() => model.type == 1),
            validateFormat: validateFormat(
                /^\d+(.\d+)?:\d+(.\d+)?(,\d+(.\d+)?:\d+(.\d+)?)*$/g,
                `Please enter in the correct format: X1:Y1,X2:Y2... Ex: 0.3:10,4:100`
            )
        },
        displayRate: {
            required,
            validateFormat: validateFormat(
                /^\d+(,\d+)*$/g,
                `Please enter in the correct format: Ex: 2,3,5 [in integer numbers]`
            )
        },
        endTime: { validateDateAfter: validateDateAfter('startTime') }
    };

    const v$ = useVuelidate(validations, model);

    const fetch = crudAction.executeAction({
        request: () => scratchTicketService.get(model.id),
        onSuccess: ({ data }) => {
            data.startTime = new Date(data.startTime);
            data.endTime = data.endTime ? new Date(data.endTime) : null;
            Object.assign(model, data);
        },
        onError: () => router.push({ name: ROUTE_NAME_SCRATCH.TICKET.INDEX })
    });

    const update = crudAction.excuteAndNotify({
        validator: v$,
        request: () =>
            scratchTicketService.update(
                model.id,
                model.quantity,
                model.freeLimit,
                model.payoutRate,
                model.adsPayoutRate,
                model.displayRate,
                model.endTime ? model.endTime.getTime() : null
            ),
        onSuccess: () => router.push({ name: ROUTE_NAME_SCRATCH.TICKET.INDEX })
    });

    return { model, v$, timeUnits, fetch, update };
}
